import React, { createContext, useContext } from 'react';
import { history, useSelector } from 'umi';
import styled from 'styled-components';
import { logger } from '@/utils/logger';
import { addOrgWsPrefix } from '@/utils';
import { trackButtonClick } from '@/utils/trackEvent';
import { formatSql } from '@/utils/formatSql';
import { NewDataset, DashboardSidebar } from '@/components/Icons';
import Folder from '@/services/folder';
import type { ReduxState } from '@/models/types';

// sessionStorage key the dataset editor reads to prefill a new dataset's SQL (see the Hub /
// EditDataset consumers). Kept in sync with those readers.
export const INSIGHTS_DATASET_QUERY_HANDOFF = 'beem.insights.datasetQueryHandoff';

// Opens the dataset editor with a SQL statement in it, unsaved: the editor reads the stash once
// on load and only seeds it (EditDatasetHOC). With `editUrl` it opens that existing dataset;
// otherwise it opens the Hub's create-dataset flow. The Hub lives at `/hub/:folderId` and bare
// `/hub` is a component-less route that only redirects once the root folder loads, so the
// workspace root folder is resolved first (falling back to bare `/hub` if that lookup fails).
export const openDatasetEditorWithSql = async ({
  sql,
  workspaceId,
  editUrl,
}: {
  sql: string;
  workspaceId?: string | null;
  editUrl?: string | null;
}) => {
  try {
    sessionStorage.setItem(INSIGHTS_DATASET_QUERY_HANDOFF, sql);
  } catch (e) {
    logger.error('Failed to stash dataset query handoff', e);
  }
  if (editUrl) {
    history.push(editUrl);
    return;
  }
  let target = '/hub';
  try {
    if (workspaceId) {
      const folder = await Folder.getRootFolder(workspaceId);
      if (folder?.id) target = `/hub/${folder.id}`;
    }
  } catch (e) {
    logger.error('Failed to resolve root folder for create dataset', e);
  }
  history.push(addOrgWsPrefix(target), { openCreateDataset: true });
};

const StepActions = styled.div<{ $marginBottom: number }>`
  display: flex;
  gap: 6px;
  margin-top: 2px;
  margin-bottom: ${({ $marginBottom }) => $marginBottom}px;
`;

// Small secondary action under a step's / answer's data — hands the context off to the dataset
// editor or the AI dashboard prompt.
const StepButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 10px;
  border: 1px solid #dadee6;
  border-radius: 6px;
  background: #fff;
  color: #0a132b;
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;

  &:hover:not(:disabled) {
    background: #f8f8fa;
    border-color: #b6c0d3;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const StepButtonIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

// Asks the agent a follow-up in the open conversation, as if typed, with what the input would send
// (ChatUI provides it). The step buttons use it so a dataset or a dashboard is made in the chat,
// where its card follows it, rather than in a page the user is sent off to. `busy` while the agent
// is still answering; null outside a chat the user can write in.
export const ChatAskContext = createContext<{
  ask: (prompt: string) => void;
  busy: boolean;
} | null>(null);

// Create an AI dashboard from a result: seed the create modal's prompt with the reasoning (the
// analysis that produced the data). Only outside a chat; in one, the agent is asked instead.
const openDashboardCreate = (reasoning?: string) => {
  const prompt = reasoning || 'Create a dashboard from this analysis.';
  history.push(addOrgWsPrefix('/dashboard'), { aiInsightsPrompt: prompt });
};

// The "Create dataset" / "Create dashboard" pair rendered below a data table — in a
// chain-of-thought step and below each table in the final answer. Dataset shows when a SQL
// statement is available; dashboard shows whenever there's a result to build from.
export const StepActionButtons = ({
  sql,
  reasoning,
  canCreateDashboard = true,
  marginBottom = 8,
}: {
  sql?: string;
  reasoning?: string;
  canCreateDashboard?: boolean;
  marginBottom?: number;
}) => {
  const workspaceId = useSelector((state: ReduxState) => state.organization.currentWorkspace);

  const chat = useContext(ChatAskContext);

  const handleCreateDataset = (sqlText?: string) => {
    if (!sqlText) return;
    trackButtonClick('insights-cot-create-dataset');
    // Laid out for reading: the query a step ran is often one long line. Only whitespace moves.
    if (chat)
      chat.ask(
        `Save this query as a new dataset, with a short descriptive name:\n\n\`\`\`sql\n${formatSql(
          sqlText,
        )}\n\`\`\``,
      );
    else openDatasetEditorWithSql({ sql: sqlText, workspaceId });
  };

  const handleCreateDashboard = (text?: string) => {
    trackButtonClick('insights-cot-create-dashboard');
    if (chat) chat.ask('Build a dashboard from this result.');
    else openDashboardCreate(text);
  };

  if (!sql && !canCreateDashboard) return null;
  return (
    <StepActions $marginBottom={marginBottom}>
      {sql && (
        <StepButton type="button" disabled={chat?.busy} onClick={() => handleCreateDataset(sql)}>
          <StepButtonIcon>
            <NewDataset height="16" width="16" />
          </StepButtonIcon>
          Create dataset
        </StepButton>
      )}
      {canCreateDashboard && (
        <StepButton
          type="button"
          disabled={chat?.busy}
          onClick={() => handleCreateDashboard(reasoning)}
        >
          <StepButtonIcon>
            <DashboardSidebar color="#0067f4" width="16" height="16" />
          </StepButtonIcon>
          Create dashboard
        </StepButton>
      )}
    </StepActions>
  );
};

export default StepActionButtons;
